/**
 * ACEPTAR —O RECHAZAR— UNA INVITACIÓN A ADMINISTRAR.
 *
 * Se llega desde el enlace del correo, con el token en el querystring.
 *
 * PIDE SESIÓN, aunque venga de un correo: el permiso se le da a una cuenta.
 * Lo garantiza `RequireAuth` en las rutas, que además se acuerda de adónde
 * iba la persona, así que quien abre el enlace sin sesión entra y vuelve acá
 * con el token intacto.
 *
 * DICE QUÉ SIGNIFICA ADMINISTRAR ANTES DE PREGUNTAR. Para mucha gente
 * «administrador» es un adorno; aprobar pagos y corregir puntos no lo es, y
 * quien va a decir que sí tiene derecho a saber a qué.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { AdminInvitationPreview } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function AdminInvitationPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [invitacion, setInvitacion] = useState<AdminInvitationPreview | null>(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [rechazada, setRechazada] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token === '') {
      setCargando(false);
      return;
    }
    let vigente = true;
    api
      .previewAdminInvitation(token)
      .then((datos) => {
        if (vigente) setInvitacion(datos);
      })
      .catch((caught: unknown) => {
        if (vigente) setError(errorMessage(t, caught));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    // Si la persona se va antes de que conteste el servidor, no se toca el
    // estado de un componente que ya no está.
    return () => {
      vigente = false;
    };
  }, [token, t]);

  async function aceptar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      const resultado = await api.acceptAdminInvitation(token);
      // Directo a lo que acaba de aceptar administrar: quedarse en esta
      // pantalla con un «listo» obligaría a buscarlo a mano.
      navigate(resultado.linkPath, { replace: true });
    } catch (caught) {
      setError(errorMessage(t, caught));
      setEnviando(false);
    }
  }

  async function rechazar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.declineAdminInvitation(token);
      setRechazada(true);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  if (token === '') {
    return (
      <AuthLayout>
        <h1>{t('adminInvite.title')}</h1>
        <Alert kind="error">{t('adminInvite.noToken')}</Alert>
        <div className="links">
          <Link to="/">{t('adminInvite.goHome')}</Link>
        </div>
      </AuthLayout>
    );
  }

  if (cargando) {
    return (
      <AuthLayout>
        <h1>{t('adminInvite.title')}</h1>
        <p className="help">{t('common.loading')}</p>
      </AuthLayout>
    );
  }

  if (rechazada) {
    return (
      <AuthLayout>
        <h1>{t('adminInvite.declinedTitle')}</h1>
        <Alert kind="success">{t('adminInvite.declinedBody')}</Alert>
        <div className="links">
          <Link to="/">{t('adminInvite.goHome')}</Link>
        </div>
      </AuthLayout>
    );
  }

  if (invitacion === null) {
    return (
      <AuthLayout>
        <h1>{t('adminInvite.title')}</h1>
        <Alert kind="error">{error ?? t('adminInvite.invalid')}</Alert>
        <div className="links">
          <Link to="/">{t('adminInvite.goHome')}</Link>
        </div>
      </AuthLayout>
    );
  }

  const queCosa =
    invitacion.scope === 'CLUB'
      ? t('adminInvite.theClub', { name: invitacion.name })
      : t('adminInvite.theTournament', {
          name: invitacion.name,
          community: invitacion.communityName ?? '',
        });

  return (
    <AuthLayout>
      <h1>{t('adminInvite.title')}</h1>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <p>{t('adminInvite.body', { who: invitacion.invitedByName, what: queCosa })}</p>
      <p className="help">{t('adminInvite.whatItMeans')}</p>

      <div className="button-row">
        <button type="button" disabled={enviando} onClick={() => void aceptar()}>
          {t('adminInvite.accept')}
        </button>
        <button type="button" className="link" disabled={enviando} onClick={() => void rechazar()}>
          {t('adminInvite.decline')}
        </button>
      </div>

      <p className="help">{t('adminInvite.noPermissionYet')}</p>
    </AuthLayout>
  );
}
